import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Router } from '@angular/router';
import {
  Subject,
  Subscription,
  catchError,
  finalize,
  firstValueFrom,
  of,
  switchMap,
} from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  CadStorageFile,
  StorageLinkedMouldingFlow,
  StorageLinkedQuoteItem,
  splitStorageFilename,
} from '../../../models/storage.model';
import { CadObject } from '../../../models/cad-object.model';
import { CadFileService } from '../../services/cad-file.service';
import { StorageService } from '../../services/storage.service';
import { ButtonModule } from 'primeng/button';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { ConfirmationService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Tooltip } from 'primeng/tooltip';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { debouncedSearchTerms } from '../../../utils/list-search';
import { CadViewerComponent } from '../../cad-viewer/cad-viewer.component';
import { CadPropertiesComponent } from '../../shared/cad-properties/cad-properties.component';
import { UploadCadComponent } from '../../shared/upload-cad/upload-cad.component';
import { SettingsService } from '../../settings-page/settings-service/settings.service';
import { CustomersService } from '../../services/customers.service';
import {
  cadUploadFileTypeError,
  isCadUploadFile,
} from '../../../models/cad-file.model';
import { DownloadService } from '../../services/download.service';
import { isInterceptorHandledError } from '../../quotes-api-error';
import { MouldSolution } from '../../../models/mould-solution.model';
import {
  displayedMouldPieces,
  mouldPieceColor,
} from '../../cad-viewer/helpers/display-mould-solution';

/** The CAD files tab of the Storage page. */
@Component({
  selector: 'app-cad-storage-tab',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    DatePipe,
    DecimalPipe,
    ButtonModule,
    SharedConfirmDialogComponent,
    DialogModule,
    ProgressSpinnerModule,
    Tooltip,
    Paginator,
    CheckboxModule,
    SelectModule,
    CadViewerComponent,
    CadPropertiesComponent,
    UploadCadComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './cad-storage-tab.component.html',
})
export class CadStorageTabComponent implements OnInit, OnDestroy {
  storageService = inject(StorageService);
  cadFileService = inject(CadFileService);
  confirmationService = inject(ConfirmationService);
  router = inject(Router);
  settingsService = inject(SettingsService);
  customersService = inject(CustomersService);
  downloadService = inject(DownloadService);
  files: CadStorageFile[] = [];
  // The first load waits for settings, which decide what can be listed.
  loading = true;
  loadError: string | null = null;
  totalRecords = 0;
  rows = 20;
  first = 0;
  includeMouldingCad = false;
  isMouldingEnabled = false;
  selectedCompanyFilter: number | 'none' | null = null;
  companyFilterOptions: Array<{
    label: string;
    value: number | 'none' | null;
  }> = [
    { label: 'All customers', value: null },
    { label: 'No customer', value: 'none' },
  ];

  viewDialogVisible = false;
  viewDialogLoading = false;
  selectedFile: CadStorageFile | null = null;
  loadedCadObjects: CadObject[] = [];
  selectedCadObject: CadObject | null = null;

  readonly mouldSolutions = signal<MouldSolution[]>([]);
  readonly mouldSolutionsLoading = signal(false);
  readonly selectedMouldSolution = signal<MouldSolution | null>(null);
  protected readonly displayedMouldPieces = displayedMouldPieces;
  protected readonly mouldPieceColor = mouldPieceColor;

  renameTarget: CadStorageFile | null = null;
  renameExtension = '';
  renameControl = new FormControl('', { nonNullable: true });
  renameInProgress = false;
  renameError: string | null = null;

  addCadDialogVisible = false;
  uploadInProgress = false;
  uploadError: string | null = null;

  searchQuery = new FormControl('', { nonNullable: true });
  activeSearch = '';

  private fileRequests = new Subject<boolean>();
  private subscriptions = new Subscription();
  private viewSubscription?: Subscription;

  ngOnInit(): void {
    this.subscriptions.add(
      this.fileRequests
        .pipe(
          switchMap((showLoadingState) => {
            if (showLoadingState) {
              this.loading = true;
            }
            return this.storageService
              .list('cad', {
                limit: this.rows,
                offset: this.first,
                forMoulding: this.forMouldingFilter,
                company: this.selectedCompanyFilter,
                search: this.activeSearch,
              })
              .pipe(
                catchError((err) => {
                  console.error('Error fetching CAD files', err);
                  return of(null);
                }),
              );
          }),
        )
        .subscribe((response) => {
          this.loading = false;
          this.files = response?.results ?? [];
          this.totalRecords = response?.count ?? 0;
          this.loadError = response ? null : 'Failed to load files.';
        }),
    );

    this.subscriptions.add(
      debouncedSearchTerms(this.searchQuery.valueChanges).subscribe(
        (search) => {
          this.activeSearch = search;
          this.first = 0;
          this.loadFiles(true);
        },
      ),
    );

    this.subscriptions.add(
      this.customersService.companies$.subscribe((companies) => {
        this.companyFilterOptions = [
          { label: 'All customers', value: null },
          { label: 'No customer', value: 'none' },
          ...companies.map((company) => ({
            label: company.name,
            value: company.id,
          })),
        ];
      }),
    );
    void this.customersService.loadCompanies().catch((error) => {
      console.error('Error loading companies', error);
    });

    // Settings decide whether moulding CAD can be listed, so load after them.
    this.subscriptions.add(
      this.settingsService.isMouldingEnabled$().subscribe((isEnabled) => {
        this.isMouldingEnabled = isEnabled;
        if (!isEnabled) {
          this.includeMouldingCad = false;
        }
        this.first = 0;
        this.loadFiles(true);
      }),
    );
  }

  /** Omitting the filter lists moulding CAD alongside quote CAD. */
  private get forMouldingFilter(): boolean | undefined {
    return this.isMouldingEnabled && this.includeMouldingCad
      ? undefined
      : false;
  }

  loadFiles(showLoadingState: boolean): void {
    this.fileRequests.next(showLoadingState);
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 20;
    this.loadFiles(true);
  }

  onIncludeMouldingCadChange(checked: boolean): void {
    if (!this.isMouldingEnabled || checked === this.includeMouldingCad) {
      return;
    }

    this.includeMouldingCad = checked;
    this.first = 0;
    this.loadFiles(true);
  }

  onCompanyFilterChange(value: number | 'none' | null): void {
    this.selectedCompanyFilter = value;
    this.first = 0;
    this.loadFiles(true);
  }

  private reloadAfterDelete(): void {
    // Deleting the last row of a later page would otherwise leave it empty.
    if (this.files.length === 1 && this.first > 0) {
      this.first = Math.max(0, this.first - this.rows);
    }
    this.loadFiles(false);
  }

  openViewDialog(file: CadStorageFile): void {
    // Cancel any earlier detail request so a late response cannot replace this one.
    this.viewSubscription?.unsubscribe();
    this.selectedFile = file;
    this.viewDialogVisible = true;
    this.viewDialogLoading = true;
    this.loadedCadObjects = [];
    this.selectedCadObject = null;
    this.resetMouldSolutions();

    this.viewSubscription = this.storageService
      .get('cad', file)
      .pipe(
        switchMap((detail) =>
          this.cadFileService.loadCadObjects(detail.cad_object_ids),
        ),
        finalize(() => {
          this.viewDialogLoading = false;
        }),
      )
      .subscribe({
        next: (loadedCadObjects) => {
          this.loadedCadObjects = loadedCadObjects;
          this.selectedCadObject = loadedCadObjects[0] ?? null;
          if (this.selectedCadObject) {
            void this.loadMouldSolutions(this.selectedCadObject.id);
          }
        },
        error: (error) => {
          console.error('Error loading file for viewer:', error);
        },
      });
    this.subscriptions.add(this.viewSubscription);
  }

  closeViewDialog(): void {
    this.viewSubscription?.unsubscribe();
    this.viewDialogVisible = false;
    this.selectedFile = null;
    this.loadedCadObjects = [];
    this.selectedCadObject = null;
    this.resetMouldSolutions();
  }

  toggleMouldSolution(solution: MouldSolution): void {
    this.selectedMouldSolution.update((selected) =>
      selected?.id === solution.id ? null : solution,
    );
  }

  private async loadMouldSolutions(cadObjectId: number): Promise<void> {
    this.mouldSolutionsLoading.set(true);
    try {
      const solutions = await firstValueFrom(
        this.cadFileService.getMouldSolutions(cadObjectId),
      );
      if (this.selectedCadObject?.id !== cadObjectId) return;
      this.mouldSolutions.set(solutions);
    } catch (error) {
      console.error('Error loading mould solutions', error);
    } finally {
      if (this.selectedCadObject?.id === cadObjectId) {
        this.mouldSolutionsLoading.set(false);
      }
    }
  }

  private resetMouldSolutions(): void {
    this.mouldSolutions.set([]);
    this.mouldSolutionsLoading.set(false);
    this.selectedMouldSolution.set(null);
  }

  openRenameDialog(file: CadStorageFile): void {
    this.renameTarget = file;
    // The extension is shown after the input and cannot be edited.
    const { stem, extension } = splitStorageFilename(file.filename);
    this.renameExtension = extension;
    this.renameControl.setValue(stem);
    this.renameError = null;
  }

  closeRenameDialog(): void {
    this.renameTarget = null;
    this.renameInProgress = false;
    this.renameError = null;
  }

  submitRename(): void {
    const target = this.renameTarget;
    const stem = this.renameControl.value.trim();
    if (!target || !stem || this.renameInProgress) {
      return;
    }

    this.renameInProgress = true;
    this.renameError = null;
    this.subscriptions.add(
      this.storageService
        .rename('cad', target, stem + this.renameExtension)
        .subscribe({
          next: () => {
            this.closeRenameDialog();
            // The renamed file may no longer match, which could empty this page.
            if (this.activeSearch) {
              this.first = 0;
            }
            this.loadFiles(false);
          },
          error: () => {
            this.renameInProgress = false;
            this.renameError = 'Failed to rename file. Please try again.';
          },
        }),
    );
  }

  confirmDelete(file: CadStorageFile): void {
    if (!file.can_delete) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Delete File',
      message: `Are you sure you want to delete "${file.filename}"? This action is irreversible.`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.subscriptions.add(
          this.storageService.delete('cad', file).subscribe({
            next: () => {
              this.reloadAfterDelete();
            },
            error: (err) => console.error('Error deleting file', err),
          }),
        );
      },
    });
  }

  navigateToQuote(linkedItem: StorageLinkedQuoteItem): void {
    const revisionNumber = linkedItem.linked_revision_number;
    this.router.navigate(['/quote', linkedItem.quote_id, revisionNumber]);
  }

  navigateToMouldingFlow(flow: StorageLinkedMouldingFlow): void {
    this.router.navigate(['/auto-molds', flow.id]);
  }

  getLinkedQuoteItemTooltip(linkedItem: StorageLinkedQuoteItem): string {
    return `Linked to ${linkedItem.linked_quote_item_name} in Revision ${linkedItem.linked_revision_number} of Quote ${linkedItem.quote_code_number}`;
  }

  async downloadFile(file: CadStorageFile): Promise<void> {
    try {
      const detail = await firstValueFrom(this.storageService.get('cad', file));
      if (!detail.file_url) {
        throw new Error('File has no stored content to download');
      }
      await this.downloadService.downloadFile(detail.file_url, {
        defaultFilename: file.filename,
        mimeType: 'application/octet-stream',
      });
    } catch (error) {
      console.error('Error downloading file', error);
    }
  }

  openAddCadDialog(): void {
    this.addCadDialogVisible = true;
    this.uploadInProgress = false;
    this.uploadError = null;
  }

  closeAddCadDialog(): void {
    this.addCadDialogVisible = false;
    this.uploadInProgress = false;
    this.uploadError = null;
  }

  onFilesSelected(files: File[]): void {
    if (!files || files.length === 0) {
      return;
    }

    const invalidFiles = files.filter((file) => !isCadUploadFile(file));

    if (invalidFiles.length > 0) {
      this.uploadError = cadUploadFileTypeError(
        invalidFiles.map((file) => file.name),
      );
      return;
    }

    this.uploadError = null;
    this.uploadInProgress = true;

    const subscription = this.cadFileService
      .postCadFiles(files, { forMoulding: false })
      .subscribe({
        next: () => {
          this.closeAddCadDialog();
          this.loadFiles(false);
        },
        error: (err: unknown) => {
          this.uploadInProgress = false;
          if (isInterceptorHandledError(err)) {
            return;
          }
          this.uploadError =
            err instanceof Error
              ? err.message
              : 'Failed to upload CAD file. Please try again.';
        },
      });

    this.subscriptions.add(subscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
